import { AccessTypeT, ScopeT } from 'business/definitions';
import { Button, styled } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { getBlocks, getScopeExpressionKey } from 'helpers/scopes';
import {
    useGetScopeBlockValueRemover,
    useGetScopeBlockValuesUpdater,
} from '../../hooks/blockValues';

import PermissionEditor from '../PermissionEditor/PermissionEditor';
import ProtectedFieldModal from '../ui/ProtectedFieldModal';
import ScopeBlocksEditor from './ScopeBlocksEditor';
import { getScopeAccessTypeButton } from '../../helpers/scopes';
import { useGetAccessTypeUpdater } from '../../hooks/accessType';
import { useTranslation } from 'react-i18next';
import { useUserCreationFormContext } from 'routes/Admin/UserManagement/Users/CreateOrEditUser/CreateUserContext/CreateUserFormContext';

const StyProtectionButton = styled(Button, { name: 'StyButton' })(
    ({ theme }) => ({
        height: '26px',
        minHeight: '26px',
        border: 'solid 1px',
        borderColor: theme.palette.primary.main,
    }),
);

interface ScopeEditorProps {
    scope: ScopeT;
}

const ScopeEditor = ({ scope }: ScopeEditorProps) => {
    const { t } = useTranslation();
    const { scopes: userScopes } = useUserCreationFormContext();

    const [openProtectedFields, setOpenProtectedFields] = useState(false);
    const [accessType, setAccessType] = useState<AccessTypeT>('no_access');

    useEffect(() => {
        const currentUserScope = userScopes.find(
            (userScope) => userScope.name === scope.name,
        );
        setAccessType(
            currentUserScope ? currentUserScope.accessType : 'no_access',
        );
    }, [scope.name]);

    const updateAccessType = useGetAccessTypeUpdater(scope);
    const onUpdateBlockValues = useGetScopeBlockValuesUpdater(scope);
    const onRemoveBlockValue = useGetScopeBlockValueRemover(scope);
    const blocks = getBlocks(scope);

    return (
        <>
            <PermissionEditor
                title={t(
                    'adminSpace.createUser.modalStepper.scopeSection.scopes',
                )}
                titleAction={
                    <StyProtectionButton
                        onClick={() => setOpenProtectedFields(true)}
                    >
                        {t('adminSpace.displayProtectedFields')}
                    </StyProtectionButton>
                }
                accessType={accessType}
                accessTypeButtons={getScopeAccessTypeButton(scope)}
                updateAccessType={(newAccessType) => {
                    updateAccessType(newAccessType);
                    setAccessType(newAccessType);
                }}
            >
                <ScopeBlocksEditor
                    scope={scope}
                    userScopes={userScopes}
                    title={t(
                        `adminSpace.createUser.modalStepper.scopeSection.expression.${getScopeExpressionKey(scope)}`,
                    )}
                    blocks={blocks}
                    onUpdateBlockValues={onUpdateBlockValues}
                    onRemoveBlockValue={onRemoveBlockValue}
                />
            </PermissionEditor>
            {openProtectedFields && (
                <ProtectedFieldModal
                    open={openProtectedFields}
                    onClose={() => setOpenProtectedFields(false)}
                    scope={scope}
                />
            )}
        </>
    );
};

export default ScopeEditor;
